import React, { useState } from 'react';
import { Image, Platform, StyleSheet, View } from 'react-native';
import { ScrollView, TouchableOpacity } from 'react-native-gesture-handler';
import { useDispatch } from 'react-redux';
import { Screens, Stacks } from '../../constants/ScreenNames';
import { addExpense, updateExpense } from '../../store/actions/expense';
import { Color, CommonProperties } from '../../styles';
import BottomButton from '../BottomButton';
import WrapBottomColor from '../containers/WrapBottomColor';
import Icon from '../Icon';
import ExpenseFormFields from '../ExpenseWidget/ExpenseFormFields';
import useExpenseWidget, { expenseActions } from '../../utils/useExpenseWidget';
import RowView from '../RowView';
import { useCameraOptions } from '../../utils/useCameraOptions';
import Camera from '../Camera';
import { scale } from '../../utils/scale.utils';
import BaseText from '../BaseText';
import { useTranslation } from 'react-i18next';
import { i18nNS, useComponentTKey } from '../../i18n';

const TripExpenseModal = ({ navigation, route }) => {
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseWidget');
  const [isOpenForm, setIsOpenForm] = useState(true);
  const reduxDispatch = useDispatch();
  const { image, expense, reportId, expenseId } = route.params || {};
  const [selectedImage, setSelectedImage] = useState(image);
  const [state, dispatch, openCalendar, validate] = useExpenseWidget(expense, {
    enableCategoryEdit: true,
    enableAttendeesEdit: true,
  });
  const [showCamera, showOptions, disableCamera] = useCameraOptions();

  const moreOptions = () => {
    showOptions(() => setSelectedImage(null));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (
      validate({
        [expenseActions.MERCHANT]: true,
        [expenseActions.SUB_CATEGORY]: true,
        [expenseActions.ATTENDEES]: true,
      })
    ) {
      if (expenseId) {
        reduxDispatch(updateExpense(reportId, expenseId, { ...state, image: selectedImage }));
        navigation.navigate(Stacks.ExpenseStack, { screen: Screens.Expense });
      } else {
        reduxDispatch(addExpense({ ...state, image: selectedImage }, reportId));
        navigation.navigate(Stacks.ExpenseStack, { screen: Screens.Expense });
      }
    }
  };

  const formStyles = isOpenForm ? null : styles.closedForm;
  const wrapFormStyles = isOpenForm ? null : styles.wrapClosedForm;
  return (
    <WrapBottomColor>
      <View style={styles.container}>
        {showCamera ? (
          <Camera
            showRotate
            showGallery
            onClose={disableCamera}
            onGetImage={(data) => {
              disableCamera();
              setSelectedImage(data.base64);
            }}
          />
        ) : null}
        <Image source={{ uri: selectedImage || '' }} style={styles.image} />
        <View style={styles.content}>
          <RowView style={styles.actions}>
            <TouchableOpacity onPress={() => navigation.goBack()}>
              <Icon name="chevronLeft" color="white" size={24} />
            </TouchableOpacity>
            <TouchableOpacity onPress={moreOptions}>
              <Icon name="redo" color="white" size={26} />
            </TouchableOpacity>
          </RowView>
          <ScrollView contentContainerStyle={[styles.scroll, wrapFormStyles]}>
            <View>
              <TouchableOpacity onPress={() => setIsOpenForm(!isOpenForm)} style={styles.toggle}>
                <BaseText style={styles.toggleText}>
                  {isOpenForm ? t(tKey('HideForm')) : t(tKey('ShowForm'))}
                </BaseText>
              </TouchableOpacity>
              <ExpenseFormFields
                style={[styles.form, formStyles]}
                state={state}
                openCalendar={openCalendar}
                dispatch={dispatch}
              />
            </View>
          </ScrollView>
        </View>
      </View>
      <BottomButton label={t(tKey('Save'))} full onPress={onSubmit} />
    </WrapBottomColor>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.gray,
  },
  content: {
    ...StyleSheet.absoluteFillObject,
    top: Platform.OS === 'web' ? 0 : scale(12),
  },
  image: {
    width: '100%',
    height: '100%',
  },
  actions: {
    justifyContent: 'space-between',
    zIndex: 1,
    backgroundColor: 'rgba(0,0,0,.09)',
    ...CommonProperties.p3,
    ...CommonProperties.mt3,
  },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 12,
  },
  form: {
    backgroundColor: 'white',
  },
  closedForm: {
    maxHeight: scale(120),
    overflow: 'hidden',
  },
  wrapClosedForm: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  toggle: {
    alignItems: 'flex-end',
    ...CommonProperties.pr3,
    ...CommonProperties.py3,
  },
  toggleText: {
    color: Color.accent,
  },
});

export default TripExpenseModal;
